import { Button, Checkbox, Divider } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { TableCardX } from 'framework/components/cards/TableCardX';
import { QueryResultPaginationX } from 'framework/components/pagination/QueryResultPaginationX';
import { SearchbarX } from 'framework/components/search/SearchbarX';
import { useQueryContext } from 'framework/hooks/useQueryContext';
import { useStateToggleBoolean } from 'framework/hooks/useStateToggleBoolean';
import { onResetToPage1 } from 'framework/types/onResetToPage1';
import { useAccordionState } from 'framework/utils/useAccordionState';
import { ILoansFilterParams, LoanExportProp, LoanExportProps, loansQuery_export } from 'gen/ApiClient';
import { useLocalization } from 'localization/useLocalization';
import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useDialogsContext } from 'shared/dialogs/useDialogsContext';
import { TextLinkExportButtonMenuAndDialog } from 'shared/export/TextLinkExportButtonMenuAndDialog';
import { PageReloadButtonX } from 'framework/components/layout/PageReloadButtonX';
import { PageSettingsButtonX } from 'framework/components/layout/PageSettingsButtonX';
import { RestrictedPageX } from 'shared/layout/RestrictedPageX';
import { LoansSettingsroute, calcLoanDetailRoute } from '../../ProcessesRoutes';
import { LoansPageBreadcrumbs } from '../LoansPageBreadcrumbs';
import { LoanForm } from '../forms/new/LoanForm';
import { LoansListFilterCard } from './LoansListFilterCard';
import { LoansListOrderBySelect } from './LoansListOrderBySelect';
import { LoansQueryContext } from './LoansQueryContext';
import { LoansTable } from './LoansTable';
import { createLoanExportRecord } from './createLoanExportRecord';
import { PageXHeaderActions } from 'framework/components/layout/PageXHeaderActions';

export const LoansListPage = () => {
	const { reload, queryResult, params, setParams } = useQueryContext(LoansQueryContext);
	const strings = useLocalization();
	const { open, confirm, cancel } = useDialogsContext(reload);
	const [expandedId, setExpandedId] = useAccordionState<string>();
	const [expandAll, toggleExpandAll] = useStateToggleBoolean(false);
	const exportRecord = useMemo(() => createLoanExportRecord(strings), [strings]);
	const { push } = useHistory();

	const onRegister = () => {
		open(
			<LoanForm
				cancel={cancel}
				confirm={id => {
					push(calcLoanDetailRoute(id));
					confirm();
				}}
			/>
		);
	};

	if (queryResult === undefined) {
		return <div></div>;
	}

	return (
		<RestrictedPageX
			pageClaim='Loans'
			maxWidth={1280}
			stickyHeader={
				<div
					className='df-col gap-8 fg1'
					style={{ paddingBottom: 16 }}>
					<div className='df-row-ac'>
						<LoansPageBreadcrumbs />
						<div className='fg1'></div>
						<PageXHeaderActions>
							<PageSettingsButtonX route={LoansSettingsroute} />
							<PageReloadButtonX reload={reload} />
						</PageXHeaderActions>
					</div>
				</div>
			}
			stickySidebar={
				<div
					className='df-col gap-16 mh100'
					style={{ minWidth: 250, padding: 16, paddingRight: 8 }}>
					<Button
						variant='outlined'
						color='primary'
						startIcon={<AddIcon />}
						onClick={onRegister}
						style={{ borderRadius: 12 }}>
						{strings.newLoan}
					</Button>
					<LoansListFilterCard
						params={params}
						setParams={setParams}
					/>
					<div className='fg1'></div>
					<Divider />
					<TextLinkExportButtonMenuAndDialog<ILoansFilterParams, LoanExportProp>
						params={params}
						filteredCount={queryResult.filteredCount}
						unfilteredCount={queryResult.unfilteredCount}
						allProps={LoanExportProps}
						downloadF={loansQuery_export}
						fileName={strings.loans}
						record={exportRecord}
						view='loans'
					/>
				</div>
			}>
			<div
				className='df-col gap-16 h100'
				style={{
					padding: 16,
					paddingLeft: 8,
				}}>
				<SearchbarX
					placeholder={strings.searchVerb}
					value={params.searchString}
					onSearch={val => setParams(onResetToPage1({ ...params, searchString: val }))}
				/>
				<TableCardX
					header={
						<div className='df-col-ae'>
							<Button
								size='small'
								endIcon={
									<Checkbox
										checked={expandAll}
										style={{ padding: 0 }}
									/>
								}
								style={{ marginRight: -2 }}
								onClick={toggleExpandAll}>
								{strings.showAllContents}
							</Button>
							<LoansListOrderBySelect
								sortParams={params.sortParams}
								onFilter={sortParams => setParams(onResetToPage1({ ...params, sortParams: sortParams }))}
							/>
						</div>
					}
					table={
						<LoansTable
							queryResult={queryResult}
							sortParams={params.sortParams}
							onFilter={sp => setParams(onResetToPage1({ ...params, sortParams: sp }))}
							expandAll={expandAll}
							expandedId={expandedId}
							toggle={setExpandedId}
						/>
					}
					footer={
						<QueryResultPaginationX
							queryResult={queryResult}
							setPageParams={pageParams => setParams({ ...params, pageParams: pageParams })}
							flexEnd
						/>
					}
				/>
			</div>
		</RestrictedPageX>
	);
};
